import React, { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import ChartHelpButton from '../ChartHelpButton';
import { getChartColor, getCleanOptions } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Market Share Summary Table
 * Shows market share comparison with delta vs last year and 3-year average
 *
 * Data comes as raw volume records with: date, year, month, iso_week, origin, volume, type, variety
 * Frontend aggregates and calculates market share and comparisons
 */
const MarketShareSummaryTable = ({ data = [], origins = [], years: yearsProp = [], commodity = '' }) => {
  const { saved, isReady: analyticsPrefsReady, isLoading: analyticsStateLoading } =
    useWidgetOptionsPersistence('market-share-summary');
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const [filtersExpanded, setFiltersExpanded] = useState(true); // Default expanded
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [monthModalOpen, setMonthModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);

  /** Set synchronously when applySummarySnapshot sets year from Firestore — blocks default-year useEffect stale closure overwriting hydrate. */
  const yearAppliedFromSnapshotRef = useRef(false);

  // Derive years from data if prop is empty (more robust)
  const derivedYears = useMemo(() => {
    if (yearsProp && yearsProp.length > 0) return yearsProp;
    if (!data || data.length === 0) return [];
    const yearsFromData = [...new Set(data.map(item => item.year).filter(Boolean))];
    return yearsFromData.sort((a, b) => b - a); // Descending
  }, [yearsProp, data]);

  // Get filter options (excluding N/A values)
  const typeOptions = useMemo(() => getCleanOptions(data, 'type', commodity), [data, commodity]);
  const varietyOptions = useMemo(() => getCleanOptions(data, 'variety', commodity), [data, commodity]);

  /** Apply snapshot fields (Firestore may use strings for numbers; options may load after first paint). */
  const applySummarySnapshot = useCallback(
    (s) => {
      // Year: unlike type/variety, we used to fall back to derivedYears[0] whenever the saved year
      // was not in `derivedYears`. That replaces e.g. 2023 from Firebase with 2026 when the current
      // dataset’s year list doesn’t include the saved year yet (or differs by commodity/date range).
      // Honor a valid numeric Firestore year; only default when missing or invalid.
      const rawY = s.selectedYear;
      const yNum = rawY == null || rawY === '' ? NaN : Number(rawY);
      if (Number.isFinite(yNum) && yNum >= 1990 && yNum <= 2100) {
        const matchYear = derivedYears.find((dy) => Number(dy) === yNum);
        const next = matchYear !== undefined ? Number(matchYear) : yNum;
        yearAppliedFromSnapshotRef.current = true;
        setSelectedYear(next);
      } else if (derivedYears.length > 0) {
        yearAppliedFromSnapshotRef.current = true;
        setSelectedYear(Number(derivedYears[0]));
      }

      if (s.selectedMonth != null && s.selectedMonth !== '') {
        const m = Number(s.selectedMonth);
        if (Number.isFinite(m) && m >= 1 && m <= 12) setSelectedMonth(m);
      }

      if ('selectedType' in s) {
        if (s.selectedType == null || s.selectedType === '') {
          setSelectedType(null);
        } else {
          const match = typeOptions.find((o) => String(o) === String(s.selectedType));
          if (match !== undefined) setSelectedType(match);
        }
      }

      if ('selectedVariety' in s) {
        if (s.selectedVariety == null || s.selectedVariety === '') {
          setSelectedVariety(null);
        } else {
          const match = varietyOptions.find((o) => String(o) === String(s.selectedVariety));
          if (match !== undefined) setSelectedVariety(match);
        }
      }

      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
    },
    [derivedYears, typeOptions, varietyOptions]
  );

  const hydrateSummary = useCallback(
    (s) => {
      applySummarySnapshot(s);
    },
    [applySummarySnapshot]
  );

  const prevTypeOptsLenRef = useRef(0);
  const prevVarietyOptsLenRef = useRef(0);
  const prevSavedHadKeysRef = useRef(false);

  // Re-apply when: (1) type/variety option lists first populate, or (2) Firestore snapshot first arrives
  // while lists already exist — otherwise hydrate may have run too early and the persist hook won't re-run.
  useLayoutEffect(() => {
    if (!analyticsPrefsReady || analyticsStateLoading) return;

    const tLen = typeOptions.length;
    const vLen = varietyOptions.length;
    const typeOptsBecameAvailable = prevTypeOptsLenRef.current === 0 && tLen > 0;
    const varietyOptsBecameAvailable = prevVarietyOptsLenRef.current === 0 && vLen > 0;
    prevTypeOptsLenRef.current = tLen;
    prevVarietyOptsLenRef.current = vLen;

    const savedKeysCount = saved && typeof saved === 'object' ? Object.keys(saved).length : 0;
    const hadSavedKeys = savedKeysCount > 0;
    const savedBecameAvailable = !prevSavedHadKeysRef.current && hadSavedKeys;

    const shouldTryApply =
      typeOptsBecameAvailable || varietyOptsBecameAvailable || savedBecameAvailable;
    if (!shouldTryApply) return;
    if (!saved || typeof saved !== 'object' || savedKeysCount === 0) return;
    if (tLen === 0) return;

    applySummarySnapshot(saved);
    prevSavedHadKeysRef.current = true;
  }, [
    analyticsPrefsReady,
    analyticsStateLoading,
    typeOptions,
    varietyOptions,
    saved,
    applySummarySnapshot,
  ]);

  useLayoutEffect(() => {
    prevTypeOptsLenRef.current = 0;
    prevVarietyOptsLenRef.current = 0;
    prevSavedHadKeysRef.current = false;
    yearAppliedFromSnapshotRef.current = false;
  }, [commodity]);

  const snapshotSummary = useCallback(
    () => ({
      selectedYear,
      selectedMonth,
      selectedType,
      selectedVariety,
      filtersExpanded,
    }),
    [selectedYear, selectedMonth, selectedType, selectedVariety, filtersExpanded]
  );

  usePersistWidgetSnapshot(
    'market-share-summary',
    commodity,
    !!(data?.length && derivedYears.length),
    hydrateSummary,
    snapshotSummary,
    [selectedYear, selectedMonth, selectedType, selectedVariety, filtersExpanded]
  );

  // Default year only after the analytics document has finished loading. While isLoading is true,
  // widgetOptions in context can still be empty even though Firestore has a saved year — applying
  // derivedYears[0] here would persist and overwrite Firebase with the default.
  //
  // Also: layout effects hydrate selectedYear from Firestore before paint, but this effect can run
  // with a stale selectedYear===null closure from the first commit — yearAppliedFromSnapshotRef
  // is set synchronously in applySummarySnapshot so we do not overwrite hydrate with derivedYears[0].
  useEffect(() => {
    if (
      !analyticsPrefsReady ||
      analyticsStateLoading ||
      derivedYears.length === 0 ||
      selectedYear !== null ||
      yearAppliedFromSnapshotRef.current
    ) {
      return;
    }
    setSelectedYear(derivedYears[0]);
  }, [analyticsPrefsReady, analyticsStateLoading, derivedYears, selectedYear]);

  // Filter data by type and variety
  const filteredData = useMemo(() => {
    return data.filter(item => {
      if (selectedType && item.type !== selectedType) return false;
      if (selectedVariety && item.variety !== selectedVariety) return false;
      return true;
    });
  }, [data, selectedType, selectedVariety]);

  // Get unique origins from filtered data
  const filteredOrigins = useMemo(() => {
    return [...new Set(filteredData.map(d => d.origin).filter(Boolean))].sort();
  }, [filteredData]);

  // Calculate market share summary data
  const summaryData = useMemo(() => {
    if (!filteredData || filteredData.length === 0 || selectedYear === null) return [];

    // Aggregate volume by year, month, and origin
    const aggregated = {};
    filteredData.forEach(item => {
      const year = item.year;
      const month = item.month;
      const origin = item.origin;
      const volume = item.volume || 0;

      if (!origin) return;

      const key = `${year}-${month}`;
      if (!aggregated[key]) {
        aggregated[key] = { origins: {}, total: 0 };
      }
      if (!aggregated[key].origins[origin]) {
        aggregated[key].origins[origin] = 0;
      }
      aggregated[key].origins[origin] += volume;
      aggregated[key].total += volume;
    });

    // Get current period data
    const currentKey = `${selectedYear}-${selectedMonth}`;
    const lastYearKey = `${selectedYear - 1}-${selectedMonth}`;
    const currentData = aggregated[currentKey];
    const lastYearData = aggregated[lastYearKey];

    if (!currentData) return [];

    // Calculate 3-year average for same month
    const threeYearAvg = {};
    const avgYears = [selectedYear - 1, selectedYear - 2, selectedYear - 3].filter(y => derivedYears.includes(y));

    filteredOrigins.forEach(origin => {
      const volumes = avgYears.map(y => {
        const key = `${y}-${selectedMonth}`;
        const periodData = aggregated[key];
        if (!periodData || !periodData.origins[origin]) return null;
        return (periodData.origins[origin] / periodData.total) * 100;
      }).filter(v => v !== null);

      if (volumes.length > 0) {
        threeYearAvg[origin] = volumes.reduce((a, b) => a + b, 0) / volumes.length;
      }
    });

    // Build summary rows
    const rows = filteredOrigins
      .map(origin => {
        const currentVolume = currentData.origins[origin] || 0;
        const currentShare = currentData.total > 0 ? (currentVolume / currentData.total) * 100 : 0;

        const lastYearVolume = lastYearData?.origins[origin] || 0;
        const lastYearShare = lastYearData?.total > 0 ? (lastYearVolume / lastYearData.total) * 100 : null;

        const deltaVsLastYear = lastYearShare !== null ? currentShare - lastYearShare : null;
        const deltaVs3YrAvg = threeYearAvg[origin] !== undefined ? currentShare - threeYearAvg[origin] : null;

        return {
          origin,
          currentVolume,
          currentShare,
          lastYearShare,
          threeYrAvgShare: threeYearAvg[origin],
          deltaVsLastYear,
          deltaVs3YrAvg,
        };
      })
      // Omit rows where both current and last year show 0% (after rounding)
      .filter(row => {
        const currentRounded = Math.round(row.currentShare);
        const lastYearRounded = row.lastYearShare !== null ? Math.round(row.lastYearShare) : 0;
        return currentRounded > 0 || lastYearRounded > 0;
      })
      .sort((a, b) => b.currentShare - a.currentShare);

    return rows;
  }, [filteredData, selectedYear, selectedMonth, filteredOrigins, derivedYears]);

  // Calculate totals
  const totals = useMemo(() => {
    const total = summaryData.reduce((acc, row) => ({
      currentVolume: acc.currentVolume + row.currentVolume,
      currentShare: acc.currentShare + row.currentShare,
    }), { currentVolume: 0, currentShare: 0 });

    return total;
  }, [summaryData]);

  // Delta badge component matching concept design
  const DeltaBadge = ({ delta }) => {
    if (delta === null || delta === undefined) {
      return <span style={{ color: theme.secondaryText }}>-</span>;
    }

    const isPositive = delta > 0.5;
    const isNegative = delta < -0.5;

    let bgColor = isDark ? 'bg-gray-700/50' : 'bg-gray-200/50';
    let textColor = isDark ? 'text-gray-400' : 'text-gray-600';
    let arrow = '→';

    if (isPositive) {
      bgColor = isDark ? 'bg-emerald-500/20' : 'bg-emerald-500/30';
      textColor = isDark ? 'text-emerald-400' : 'text-emerald-600';
      arrow = '↑';
    } else if (isNegative) {
      bgColor = isDark ? 'bg-red-500/20' : 'bg-red-500/30';
      textColor = isDark ? 'text-red-400' : 'text-red-600';
      arrow = '↓';
    }

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${bgColor} ${textColor}`}>
        {arrow} {delta > 0 ? '+' : ''}{delta.toFixed(0)}%
      </span>
    );
  };

  // Trend icon for last column
  const TrendIcon = ({ delta }) => {
    if (delta === null || delta === undefined) {
      return <Minus size={14} style={{ color: theme.secondaryText }} />;
    }
    if (delta > 0.5) {
      return <TrendingUp size={14} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />;
    }
    if (delta < -0.5) {
      return <TrendingDown size={14} className={isDark ? 'text-red-400' : 'text-red-600'} />;
    }
    return <Minus size={14} style={{ color: theme.secondaryText }} />;
  };

  const helpDescription = "Shows each region's share of total market volume for the selected period. Compare current market share against last year and the 3-year historical average to identify growing or declining origins.";

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col bg-transparent">
        <div className="border-y py-2 px-4 flex-shrink-0" style={{ borderColor: theme.borderColor }}>
          <div className="flex items-center gap-1">
            <h3 className="uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4, color: theme.titleColor }}>
              Market Share Summary
            </h3>
            <ChartHelpButton title="Market Share Summary" description={helpDescription} />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-center" style={{ fontSize, color: theme.secondaryText }}>
            No market share data available.<br />Select a different commodity.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-transparent">
      <div className="px-4">
      <div className="border-b py-2 flex-shrink-0" style={{ borderColor: theme.borderColor }}>
        <div className="flex items-center gap-1">
          <h3 className="uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4, color: theme.titleColor }}>
            Market Share Summary - {MONTH_LABELS[selectedMonth - 1]} {selectedYear || '...'}
          </h3>
          <ChartHelpButton title="Market Share Summary" description={helpDescription} />
        </div>
      </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 px-4">
        {/* Filters Header */}
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="w-full flex items-center justify-between py-2 border-b flex-shrink-0"
          style={{ borderColor: theme.borderSecondary }}
        >
          <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>Filters</span>
          {filtersExpanded ? <ChevronUp size={16} style={{ color: theme.secondaryText }} /> : <ChevronDown size={16} style={{ color: theme.secondaryText }} />}
        </button>

        {filtersExpanded && (
          <div className="py-2 space-y-2 flex-shrink-0">
            <div className="grid grid-cols-2 gap-2">
              <FilterDropdown
                label="Year"
                value={selectedYear || '-'}
                onClick={() => setYearModalOpen(true)}
              />
              <FilterDropdown
                label="Month"
                value={MONTH_LABELS[selectedMonth - 1]}
                onClick={() => setMonthModalOpen(true)}
              />
            </div>
            {(typeOptions.length > 0 || varietyOptions.length > 0) && (
              <div className="grid grid-cols-2 gap-2">
                {typeOptions.length > 0 && (
                  <FilterDropdown
                    label="Type"
                    value={selectedType || 'All'}
                    onClick={() => setTypeModalOpen(true)}
                  />
                )}
                {varietyOptions.length > 0 && (
                  <FilterDropdown
                    label="Variety"
                    value={selectedVariety || 'All'}
                    onClick={() => setVarietyModalOpen(true)}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Summary Table - Concept Design */}
        <div className="flex-1 min-h-0 overflow-auto mt-2">
          <table className="w-full text-xs">
            <thead className="sticky top-0" style={{ backgroundColor: theme.stickyHeaderBg }}>
              <tr>
                <th className="text-left p-2 font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>Region</th>
                <th className="text-center p-2 font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>
                  {MONTH_LABELS[selectedMonth - 1]} {selectedYear}
                </th>
                <th className="text-center p-2 font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>
                  {MONTH_LABELS[selectedMonth - 1]} {selectedYear - 1}
                </th>
                <th className="text-center p-2 font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>Δ vs LY</th>
                <th className="text-center p-2 font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>Hist Avg</th>
                <th className="text-center p-2 font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>Δ vs Hist</th>
                <th className="text-center p-2 font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>Trend</th>
              </tr>
            </thead>
            <tbody>
              {summaryData.map((row, idx) => (
                <tr key={row.origin} className="border-b hover:opacity-90" style={{ borderColor: theme.tableRowBorder }}>
                  {/* Region with color dot */}
                  <td className="p-3" style={{ fontSize: fontSize - 1, color: theme.barLabelFill }}>
                    <span
                      className="inline-block w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: getChartColor(idx, summaryData.length) }}
                    />
                    {row.origin}
                  </td>
                  {/* Current Year Share (bold) */}
                  <td className="text-center font-semibold p-3" style={{ fontSize: fontSize, color: theme.barLabelFill }}>
                    {row.currentShare.toFixed(0)}%
                  </td>
                  {/* Last Year Share */}
                  <td className="text-center p-3" style={{ fontSize: fontSize - 1, color: theme.secondaryText }}>
                    {row.lastYearShare !== null ? `${row.lastYearShare.toFixed(0)}%` : '-'}
                  </td>
                  {/* Delta vs Last Year Badge */}
                  <td className="text-center p-3">
                    <DeltaBadge delta={row.deltaVsLastYear} />
                  </td>
                  {/* Historical Average */}
                  <td className="text-center p-3" style={{ fontSize: fontSize - 1, color: theme.secondaryText }}>
                    {row.threeYrAvgShare !== undefined ? `${row.threeYrAvgShare.toFixed(0)}%` : '-'}
                  </td>
                  {/* Delta vs Historical Badge */}
                  <td className="text-center p-3">
                    <DeltaBadge delta={row.deltaVs3YrAvg} />
                  </td>
                  {/* Trend Icon */}
                  <td className="text-center p-3">
                    <div className="flex justify-center">
                      <TrendIcon delta={row.deltaVsLastYear} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FilterModal
        isOpen={yearModalOpen}
        onClose={() => setYearModalOpen(false)}
        title="Select Year"
        options={derivedYears.map(String)}
        selectedValues={selectedYear ? String(selectedYear) : ''}
        onSelect={(yearStr) => { setSelectedYear(Number(yearStr)); }}
      />

      <FilterModal
        isOpen={monthModalOpen}
        onClose={() => setMonthModalOpen(false)}
        title="Select Month"
        options={MONTH_LABELS}
        selectedValues={MONTH_LABELS[selectedMonth - 1]}
        onSelect={(monthLabel) => { setSelectedMonth(MONTH_LABELS.indexOf(monthLabel) + 1); }}
      />

      {typeOptions.length > 0 && (
        <FilterModal
          isOpen={typeModalOpen}
          onClose={() => setTypeModalOpen(false)}
          title="Select Type"
          options={typeOptions}
          selectedValues={selectedType || ''}
          onSelect={(type) => { setSelectedType(type); }}
        />
      )}

      {varietyOptions.length > 0 && (
        <FilterModal
          isOpen={varietyModalOpen}
          onClose={() => setVarietyModalOpen(false)}
          title="Select Variety"
          options={varietyOptions}
          selectedValues={selectedVariety || ''}
          onSelect={(variety) => { setSelectedVariety(variety); }}
        />
      )}
    </div>
  );
};

export default MarketShareSummaryTable;
